import { isValidCNPJ, formatPhone } from "./index";

// Dados da empresa em /config: o que a tela mostra, o que valida e o corpo do
// PUT /v1/companies/:id. O PUT apaga o endereço que não vier e substitui
// parameters e colors inteiros, então o corpo sai sempre completo.

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

/** "12345678000190" → "12.345.678/0001-90"; o InputCnpj da lib só mascara ao digitar. */
function formatCnpjInput(v) {
  const d = onlyDigits(v);
  return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : String(v || "");
}

/** CEP no formato do InputCEP ("80000-000"). O que não tem 8 dígitos (ex.: "__.___-___") vira vazio. */
export function formatZipCode(v) {
  const d = onlyDigits(v);
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : "";
}

// Na tela, só a primária (cor de ação) e a secundária (a ação quando a primária é
// verde ou vermelha). A textColor não pinta nada nas telas refeitas (o --ow-heading
// não tem quem leia): sai da tela, mas a já salva vai de volta como está.
export const COLOR_KEYS = ["primary", "secondary"];

/** "#abc", "aabbcc" ou "#AABBCC" → "#AABBCC"; vazio → ""; inválido → null. */
export function normalizeHex(v) {
  let h = String(v || "").trim().replace(/^#/, "");
  if (!h) return "";
  if (/^[0-9a-f]{3}$/i.test(h)) h = h.split("").map((c) => c + c).join("");
  return /^[0-9a-f]{6}$/i.test(h) ? `#${h.toUpperCase()}` : null;
}

/** Estado da tela a partir do company-data. */
export function companyFromApi(data = {}) {
  const colors = data.colors || {};
  return {
    data: {
      name: data.name || "",
      cnpj: formatCnpjInput(data.cnpj),
      phone: formatPhone(data.phone),
      zip_code: formatZipCode(data.zip_code),
      address: data.address || "",
      number: data.number || "",
      location: data.location || "",
      city: data.city || "",
      state: data.state || "",
      permitAnonymous: data.parameters?.permitAnnonymousDeclarations === true,
    },
    colors: Object.fromEntries(COLOR_KEYS.map((key) => [key, normalizeHex(colors[key]) || ""])),
    savedColors: colors,
    parameters: data.parameters || {},
    logos: data.logos || {},
  };
}

/**
 * Erros da aba Dados por id do campo, na ordem da tela. O CNPJ só é conferido quando
 * muda, como no back: um CNPJ antigo fora do padrão não impede salvar o resto.
 */
export function validateCompanyData(values, saved = {}) {
  const errors = {};
  if (!String(values.name || "").trim()) errors["company-name"] = "Informe o nome da empresa.";

  const cnpj = onlyDigits(values.cnpj);
  if (!cnpj) errors["company-cnpj"] = "Informe o CNPJ.";
  else if (cnpj !== onlyDigits(saved.cnpj) && !isValidCNPJ(cnpj)) {
    errors["company-cnpj"] = "CNPJ inválido: confira os números.";
  }

  const phone = onlyDigits(values.phone);
  if (phone && phone.length !== 10 && phone.length !== 11) errors["company-phone"] = "Telefone incompleto.";

  const zip = onlyDigits(values.zip_code);
  if (zip && zip.length !== 8) errors["company-zip"] = "CEP incompleto.";
  return errors;
}

/** Erros das cores por id do campo: vazio é o padrão OneWork. */
export function validateColors(colors) {
  const errors = {};
  COLOR_KEYS.forEach((key) => {
    if (normalizeHex(colors[key]) === null) errors[`color-${key}`] = "Use o formato #RRGGBB.";
  });
  return errors;
}

/**
 * Corpo do PUT: o que já está salvo com o que a aba mudou (`data` ou `colors`).
 * Dos parâmetros, só o das declarações anônimas muda; os outros vão como estão.
 * Das cores, as da tela (vazia sai do objeto e volta ao padrão OneWork) sobre as
 * já salvas que a tela não mostra (textColor).
 */
export function companyPayload(saved, { data = saved.data, colors = saved.colors } = {}) {
  const trim = (v) => String(v || "").trim();
  return {
    name: trim(data.name),
    cnpj: trim(data.cnpj),
    phone: onlyDigits(data.phone),
    zip_code: formatZipCode(data.zip_code),
    address: trim(data.address),
    number: trim(data.number),
    location: trim(data.location),
    city: trim(data.city),
    state: trim(data.state),
    parameters: { ...(saved.parameters || {}), permitAnnonymousDeclarations: !!data.permitAnonymous },
    colors: {
      ...Object.fromEntries(Object.entries(saved.savedColors || {}).filter(([key]) => !COLOR_KEYS.includes(key))),
      ...Object.fromEntries(COLOR_KEYS.map((key) => [key, normalizeHex(colors[key])]).filter(([, value]) => value)),
    },
  };
}

export const LOGO_MAX_BYTES = 1024 * 1024;
export const LOGO_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
};

/** Mesmas regras do back: PNG, JPG ou WEBP, até 1 MB. */
export function logoFileError(file) {
  if (!file || !LOGO_ACCEPT[file.type]) return "Envie uma imagem PNG, JPG ou WEBP.";
  if (file.size > LOGO_MAX_BYTES) return "A imagem deve ter até 1 MB.";
  return null;
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const DEFAULT_PRIMARY = "#000229";

/**
 * O que o motor de tema ajustou nas cores escolhidas, em texto para a prévia.
 * `theme`: o resultado do deriveTenantTheme(colors).
 */
export function themeNotices(theme, colors) {
  if (!theme?.vars) return [];
  const notices = [];
  const primary = normalizeHex(colors.primary);
  const secondary = normalizeHex(colors.secondary);

  if (theme.guard === "secondary") {
    notices.push("A cor primária é parecida com o verde ou o vermelho dos status: os botões usam a cor secundária.");
  } else if (theme.guard === "default") {
    notices.push(
      "A cor primária é parecida com o verde ou o vermelho dos status: os botões usam o azul OneWork. Para usar uma cor sua, escolha uma secundária de outro tom."
    );
  }

  const action = theme.guard === "secondary" ? secondary : theme.guard === "default" ? DEFAULT_PRIMARY : primary || DEFAULT_PRIMARY;
  const fill = theme.vars["--ow-primary"];
  if (action && fill && fill.toUpperCase() !== action.toUpperCase()) {
    notices.push(`Os botões ficam em ${fill.toUpperCase()}, um tom ajustado para o texto deles continuar legível.`);
  }
  return notices;
}
